import { clsx } from 'clsx';
import { Popover, PopoverPanel, PopoverButton } from '@headlessui/react';

import { RealEstateDict } from '@monoimin/intl';

import { Icons } from '@monoimin/atoms';

import { Range } from './Range';

import type { RangeFormatter, RangeProps } from '../types';

type SelectRangeProps = Pick<
  RangeProps,
  'onChange' | 'onKeyDown' | 'min' | 'max' | 'onMouseUp' | 'step' | 'value'
> & {
  placeholder: string;
  fromPlaceholder: string;
  toPlaceholder: string;
  classNames?: {
    container?: string;
  };
  onReset?: () => void;
  valueFormatter?: RangeFormatter;
  realEstateDict: RealEstateDict;
  hideSlider?: boolean;
  hideInput?: boolean;
};

const defineSelectedValueTitle = (
  value: RangeProps['value'],
  min: RangeProps['min'],
  max: RangeProps['max'],
  placeholder: string,
  valueFormatter?: SelectRangeProps['valueFormatter']
) => {
  const isValidRangeValue = Array.isArray(value);

  if (!isValidRangeValue) {
    return placeholder;
  }

  if (!value[0] && !value[1]) {
    return placeholder;
  }

  if (value[0] === min && value[1] === max) {
    return placeholder;
  }

  if (typeof valueFormatter === 'function') {
    return valueFormatter(value);
  }

  return `${value[0]}-${value[1]}`;
};

export const SelectRange = ({
  value,
  onChange,
  min,
  max,
  step,
  placeholder,
  fromPlaceholder,
  toPlaceholder,
  classNames,
  onReset,
  valueFormatter,
  onMouseUp,
  onKeyDown,
  realEstateDict,
  hideSlider,
  hideInput,
}: SelectRangeProps) => {
  const selectedValueTitle = defineSelectedValueTitle(
    value,
    min,
    max,
    placeholder,
    valueFormatter
  );

  return (
    <Popover
      className={clsx('select-range select body', classNames?.container)}
      as='div'
    >
      <PopoverButton className='select__button bg-white group'>
        <span className='whitespace-nowrap truncate'>{selectedValueTitle}</span>
        <Icons
          icon='dropdown'
          sizeClassName='h-7 w-7'
          className='group-data-[open]:rotate-180 ease-out duration-200 shrink-0'
          aria-hidden
        />
      </PopoverButton>
      <PopoverPanel
        transition
        className='select-range__panel select__options min-w-[400px] p-5 origin-top transition data-[closed]:scale-95 data-[closed]:opacity-0 duration-100'
      >
        <Range
          min={min}
          max={max}
          value={value}
          onChange={onChange}
          onMouseUp={onMouseUp}
          onKeyDown={onKeyDown}
          step={step}
          hideSlider={hideSlider}
          hideInput={hideInput}
          fromPlaceholder={fromPlaceholder}
          toPlaceholder={toPlaceholder}
        />
        <div className='flex justify-between body mt-5'>
          <span className='select-range__panel-value whitespace-nowrap'>
            {selectedValueTitle}
          </span>
          <button
            className='select-range__reset-button button'
            onClick={onReset}
          >
            {realEstateDict.reset}
          </button>
        </div>
      </PopoverPanel>
    </Popover>
  );
};
